import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Users, DollarSign, Zap, Target, CheckCircle, TrendingUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

export default function ClevelandWholesalersPage() {
  const benefits = [
    {
      icon: Users,
      title: 'Active Buyer Pool',
      description: 'Cleveland has one of the most active investor communities in the Midwest. Your deals get seen by serious buyers.'
    },
    {
      icon: Zap,
      title: 'Fast-Moving Market',
      description: 'Good deals in Cleveland move quickly. Our platform gets your listings in front of buyers instantly.'
    },
    {
      icon: DollarSign,
      title: 'Strong Assignment Fees',
      description: 'Cleveland\'s price points allow for healthy wholesale spreads. Average assignment fees of $8-15K.'
    },
    {
      icon: Target,
      title: 'Less Competition',
      description: 'Compared to coastal markets, Cleveland offers more opportunities with less wholesaler saturation.'
    }
  ];

  const hotZips = ['44102', '44109', '44111', '44113', '44135', '44144'];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1546168/pexels-photo-1546168.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Cleveland real estate"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-6">
              <MapPin className="h-5 w-5 text-[#5DADE2]" />
              <span className="text-[#5DADE2] font-medium">Cleveland, Ohio</span>
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
              Wholesale Deals in Cleveland
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              Post your Cleveland wholesale deals to our network of active investors. Get your properties under contract moved fast.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-blue-600 text-white rounded-full px-8 text-lg h-14">
                <Link to="/post-deal">
                  Post a Cleveland Deal
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 text-lg h-14">
                <Link to="/signup?role=wholesaler">
                  Join as Wholesaler
                </Link>
              </Button>
            </div>

            <div className="flex items-center gap-6 pt-8 border-t border-white/20">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-slate-300">Free to post</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-slate-300">Direct buyer access</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span className="text-slate-300">No fees on assignments</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Wholesale in Cleveland?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Cleveland is a wholesaler's dream market with active buyers and consistent deal flow.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-[#5DADE2]/10 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="h-6 w-6 text-[#5DADE2]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Zip Codes */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Hot Zip Codes for Wholesaling
            </h2>
            <p className="text-slate-600">These Cleveland zips have the highest buyer activity</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {hotZips.map((zip, i) => (
              <div key={i} className="px-6 py-4 bg-[#5DADE2]/10 border border-[#5DADE2]/20 rounded-xl">
                <div className="text-2xl font-bold text-[#5DADE2]">{zip}</div>
                <div className="text-sm text-slate-500">Cleveland, OH</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Move Your Cleveland Deals Fast
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#5DADE2] rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">1</div>
              <h3 className="text-xl font-semibold text-white mb-3">Post Your Deal</h3>
              <p className="text-slate-400">List your Cleveland property with photos and numbers. Takes less than 5 minutes.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#5DADE2] rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">2</div>
              <h3 className="text-xl font-semibold text-white mb-3">Buyers Find You</h3>
              <p className="text-slate-400">Local and national investors browsing Cleveland deals will see your listing.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#5DADE2] rounded-full flex items-center justify-center mx-auto mb-6 text-2xl font-bold text-white">3</div>
              <h3 className="text-xl font-semibold text-white mb-3">Close & Collect</h3>
              <p className="text-slate-400">Negotiate directly with buyers and keep 100% of your assignment fee.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#5DADE2]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Start Wholesaling in Cleveland
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join the top wholesalers already using NextFlip in Cleveland
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-[#5DADE2] hover:bg-blue-50 rounded-full px-8 text-lg h-14">
              <Link to="/signup?role=wholesaler">
                Create Free Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 text-lg h-14">
              <Link to="/post-deal">
                Post a Deal Now
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
